<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  server: { type: Object, required: true },
  isRefreshing: { type: Boolean, default: false },
});

const emit = defineEmits(['refresh', 'edit', 'delete']);

const { t } = useI18n();

const toolCount = computed(
  () =>
    (Array.isArray(props.server.discovered_tools)
      ? props.server.discovered_tools
      : []
    ).length
);

// Three discovery states drive the status pill / message.
//   - error: discovery failed; show the error
//   - never: never discovered; prompt admin to refresh
//   - stale: succeeded > 7 days ago; suggest refresh
//   - ok: discovered recently; show tool count
const status = computed(() => {
  if (props.server.discovery_error) return 'error';
  if (!props.server.discovered_at) return 'never';
  if (props.server.discovery_stale) return 'stale';
  return 'ok';
});

const statusClass = computed(() => {
  switch (status.value) {
    case 'error':
      return 'bg-n-ruby-2 text-n-ruby-11';
    case 'never':
      return 'bg-n-amber-2 text-n-amber-11';
    case 'stale':
      return 'bg-n-amber-2 text-n-amber-11';
    default:
      return 'bg-n-teal-2 text-n-teal-11';
  }
});

const statusLabel = computed(() => {
  switch (status.value) {
    case 'error':
      return t('CAPTAIN.MCP_SERVERS.STATUS.ERROR');
    case 'never':
      return t('CAPTAIN.MCP_SERVERS.STATUS.NEVER');
    case 'stale':
      return t('CAPTAIN.MCP_SERVERS.STATUS.STALE');
    default:
      return t('CAPTAIN.MCP_SERVERS.STATUS.OK', { count: toolCount.value });
  }
});

const transportLabel = computed(() => {
  return props.server.transport === 'sse'
    ? t('CAPTAIN.MCP_SERVERS.FORM.TRANSPORT.OPTIONS.SSE')
    : t('CAPTAIN.MCP_SERVERS.FORM.TRANSPORT.OPTIONS.STREAMABLE_HTTP');
});
</script>

<template>
  <div
    class="flex flex-col gap-3 p-5 border rounded-lg border-n-weak bg-n-alpha-black1"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1 min-w-0">
        <div class="flex items-center gap-2">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ server.name }}
          </h3>
          <span
            v-if="!server.enabled"
            class="px-2 py-0.5 text-xs rounded-full bg-n-slate-3 text-n-slate-11"
          >
            {{ t('CAPTAIN.MCP_SERVERS.DISABLED') }}
          </span>
        </div>
        <p class="text-xs text-n-slate-11 truncate font-mono">
          {{ server.url }}
        </p>
        <p class="text-xs text-n-slate-11">{{ transportLabel }}</p>
      </div>

      <div class="flex items-center gap-2 shrink-0">
        <span
          class="px-2 py-1 text-xs font-medium rounded-md whitespace-nowrap"
          :class="statusClass"
        >
          {{ statusLabel }}
        </span>
      </div>
    </div>

    <div
      v-if="server.discovery_error"
      class="px-3 py-2 text-xs rounded-md bg-n-ruby-2 text-n-ruby-11 font-mono"
    >
      {{ server.discovery_error }}
    </div>

    <div v-if="status === 'ok' && toolCount > 0" class="flex flex-wrap gap-1.5">
      <span
        v-for="tool in server.discovered_tools.slice(0, 8)"
        :key="tool.name"
        class="px-2 py-0.5 text-xs rounded bg-n-slate-3 text-n-slate-11"
        :title="tool.description"
      >
        {{ tool.name }}
      </span>
      <span v-if="toolCount > 8" class="px-2 py-0.5 text-xs text-n-slate-11">
        {{ t('CAPTAIN.MCP_SERVERS.MORE_TOOLS', { count: toolCount - 8 }) }}
      </span>
    </div>

    <div
      class="flex items-center justify-end gap-2 pt-2 border-t border-n-weak"
    >
      <Button
        size="xs"
        variant="ghost"
        :label="t('CAPTAIN.MCP_SERVERS.ACTIONS.REFRESH')"
        :is-loading="isRefreshing"
        @click="emit('refresh', server.id)"
      />
      <Button
        size="xs"
        variant="ghost"
        :label="t('CAPTAIN.MCP_SERVERS.ACTIONS.EDIT')"
        @click="emit('edit', server)"
      />
      <Button
        size="xs"
        variant="ghost"
        color="ruby"
        :label="t('CAPTAIN.MCP_SERVERS.ACTIONS.DELETE')"
        @click="emit('delete', server)"
      />
    </div>
  </div>
</template>
